import {
    Badge,
    Button,
    Col,
    Form,
    Modal,
    Radio,
    Row,
    Select,
    Space,
    Typography,
    DatePicker,
} from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import DpaSelected from './DpaSelected';
import useFetch from "../../hooks/useFetch";

const { Paragraph } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface Props {
    filters: any;
    setFilters: (filters: any) => void;
    setFilterApply: (data: any) => void;
    communityWorkers?: any[];
    excludeFilters?: string[];
}

export default function FilterModal({
                                        filters,
                                        setFilters,
                                        setFilterApply,
                                        communityWorkers,
                                        excludeFilters = [],
                                    }: Props) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [open, setOpen] = useState(false);
    const { fetchApi } = useFetch();

    const [communityWorker, setCwByCountry] = useState<any[]>([]);
    const [interviewers, setInterviewers] = useState<any[]>([]);

    useEffect(() => {
        fetchApi({ url: '/comms/get_community_workers_by_country', method: 'GET' }).then((res) => {
            if (res?.data?.status === 'success') {
                setCwByCountry(res.data.data);
            }
        });

        fetchApi({ url: '/comms/get_interviewers', method: 'GET' }).then((res) => {
            if (res?.data?.status === 'success') {
                setInterviewers(res.data.data);
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isVisible = (key: string) => !excludeFilters.includes(key);

    const cleanFilters = () => {
        form.resetFields();
        setFilters({});
        setFilterApply({});
        setOpen(false);
    };

    const onFinish = (values: any) => {
        
        const parsedFilters: any = {};

        if (values?.communityWorker) {
            const parts = values.communityWorker.split('-');
            parsedFilters.communityWorker = parts[parts.length - 1].trim();
        }

        if (values?.dpa?.length > 0) {
            parsedFilters.dpas = values.dpa.map((dpaId: any) => ({
                dpaId: dpaId?.dpaId || dpaId,
            }));
        }

        if (values?.puCode) {
            const data = JSON.parse(values?.puCode);
            parsedFilters.dpaId = { dpaId: data?.dpaId};
        }

        //puOnly
        if (values?.puOnly) {
            const data = JSON.parse(values?.puOnly);
            console.log(values, data);
            parsedFilters.puCode = data?.dpaCode;
        }

        if (values?.genre) {
            parsedFilters.genre = values.genre;
        }

        if (values?.statusId) {
            parsedFilters.statusId = values.statusId;
        }

        if (values?.lastDays && values?.lastDays > 0) {
            parsedFilters.lastDays = values.lastDays;
        }

        if (values?.rangeInterviewDate) {
            const [date1, date2] = values.rangeInterviewDate;
            parsedFilters.rangeInterviewDate = {
                value1: moment(date1).format('YYYY-MM-DD'),
                value2: moment(date2).format('YYYY-MM-DD'),
            };
        }

        if (values?.rangeUpdatedAt) {
            const [start, end] = values.rangeUpdatedAt;
            parsedFilters.rangeUpdatedAt = {
                value1: moment(start).format('YYYY-MM-DD'),
                value2: moment(end).format('YYYY-MM-DD'),
            };
        }

        if (values?.year) {
            parsedFilters.year = values.year;
        }

        if (values?.month) {
            parsedFilters.month = values.month;
        }

        if (values?.withPhotos !== undefined) {
            parsedFilters.withPhotos = values.withPhotos;
        }

        if (values?.communityWorker) {
            parsedFilters.communityWorker = values.communityWorker;
        }

        if (values?.interviewer) {
            parsedFilters.interviewer = values.interviewer;
        }

        setFilters((prev: any) => ({ ...prev, ...parsedFilters }));
        setOpen(false);
    };

    return (
        <>
            <Badge dot={Object.keys(filters)?.length > 0}>
                <Button
                    type="primary"
                    ghost
                    icon={<FileExcelOutlined />}
                    onClick={() => setOpen(true)}
                >
                    {t('filters')}
                </Button>
            </Badge>

            <Modal
                width={600}
                title={t('filters')}
                open={open}
                onCancel={() => setOpen(false)}
                footer={
                    <Row justify="space-between">
                        <Col>
                            <Button shape="round" type="primary" danger ghost onClick={cleanFilters}>
                                {t('clearFilters')}
                            </Button>
                        </Col>
                        <Col>
                            <Space>
                                <Button shape="round" onClick={() => setOpen(false)}>
                                    {t('cancel')}
                                </Button>
                                <Button shape="round" type="primary" onClick={() => form.submit()}>
                                    {t('applyFilters')}
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                }
            >
                <Form layout="vertical" form={form} onFinish={onFinish}>
                    <Row gutter={[10, 0]}>
                        {isVisible('genre') && (
                            <Col span={24} md={12}>
                                <Form.Item name="genre" label={t('gender')}>
                                    <Select allowClear>
                                        <Option value="Male">{t('male')}</Option>
                                        <Option value="Female">{t('female')}</Option>
                                        <Option value="">{t('all')}</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {isVisible('year') && (
                            <Col span={24} md={12}>
                                <Form.Item name="year" label={t('year')}>
                                    <Select allowClear>
                                        {Array.from({ length: new Date().getFullYear() - 2000 + 2 }, (_, i) => 2000 + i)
                                            .reverse()
                                            .map((year) => (
                                                <Option key={year} value={year}>
                                                    {year}
                                                </Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {isVisible('month') && (
                            <Col span={24} md={12}>
                                <Form.Item name="month" label={t('month')}>
                                    <Select allowClear>
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                                            <Option key={month} value={month}>
                                                {moment().month(month - 1).format('MMMM')}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {isVisible('communityWorker') && communityWorkers && (
                            <Col span={24} md={12}>
                                <Form.Item name="communityWorker" label={t('fdc')}>
                                    <Select allowClear showSearch>
                                        {communityWorkers.map((w: any, i) => (
                                            <Option key={i} value={w?.assCommunityWorker}>
                                                {w?.assCommunityWorker}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {isVisible('communityWorker') && (
                            <Col span={24} md={12}>
                                <Form.Item name="communityWorker" label={t('communityWorker')}>
                                    <Select allowClear showSearch>
                                        {communityWorker.map((item, i) => (
                                            <Option key={i} value={item.assCommunityWorkerCode}>
                                                {item.assCommunityWorkerName}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {isVisible('interviewer') && (
                            <Col span={24} md={12}>
                                <Form.Item name="interviewer" label={t('interviewer')}>
                                    <Select allowClear showSearch>
                                        {interviewers.map((item, i) => (
                                            <Option key={i} value={item.interviewer}>
                                                {item.interviewer}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {isVisible('statusId') && (
                            <Col span={24} md={12}>
                                <Form.Item name="statusId" label={t('status')}>
                                    <Select allowClear>
                                        <Option value={1}>{t('status_1')}</Option>
                                        <Option value={2}>{t('status_2')}</Option>
                                        <Option value={3}>{t('status_3')}</Option>
                                        <Option value={4}>{t('status_4')}</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {isVisible('withPhotos') && (
                            <Col span={24} md={12}>
                                <Form.Item name="withPhotos" label={t('withPhotos')}>
                                    <Select allowClear>
                                        <Option value={true}>{t('yes')}</Option>
                                        <Option value={false}>{t('no')}</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {isVisible('dpa') && (
                            <Col span={24}>
                                <Paragraph strong>{t('place')}</Paragraph>
                                <DpaSelected
                                    form={form}
                                    setFilterApply={setFilterApply}
                                    filterApply={filters}
                                    isRequired={false}
                                />
                            </Col>
                        )}

                        {isVisible('puCode') && (
                            <Col span={24}>
                                <Paragraph strong>{t('place')}</Paragraph>
                                <DpaSelected form={form} name='puCode' maxLevel={3} setFilterApply={setFilterApply} filterApply={filters} isRequired={false} />
                            </Col>
                        )}

                        { /* Permite la seleccion solo hasta el nivel de PU puOnly */}
                        {isVisible('puOnly') && (
                            <Col span={24}>
                                <Paragraph strong>{t('place')}</Paragraph>
                                <DpaSelected
                                    form={form}
                                    maxLevel={2}
                                    setFilterApply={setFilterApply}
                                    filterApply={filters}
                                    isRequired={false}
                                    name='puOnly'
                                />
                            </Col>
                        )}

                        {isVisible('rangeUpdatedAt') && (
                            <Col span={24}>
                                <Form.Item name="rangeUpdatedAt" label={t('createdAt')}>
                                    <RangePicker />
                                </Form.Item>
                            </Col>
                        )}

                        {isVisible('rangeInterviewDate') && (
                            <Col span={24}>
                                <Form.Item name="rangeInterviewDate" label={t('interviewDate')}>
                                    <RangePicker />
                                </Form.Item>
                            </Col>
                        )}

                        {isVisible('lastDays') && (
                            <Col span={24}>
                                <Form.Item name="lastDays" label={t('showLast')}>
                                    <Radio.Group defaultValue={0}>
                                        <Radio.Button value={1}>{t('last24Hours')}</Radio.Button>
                                        <Radio.Button value={7}>{t('last7Days')}</Radio.Button>
                                        <Radio.Button value={30}>{t('last1Month')}</Radio.Button>
                                        <Radio.Button value={90}>{t('last3Months')}</Radio.Button>
                                        <Radio.Button value={365}>{t('last1Year')}</Radio.Button>
                                        <Radio.Button value={0}>{t('all')}</Radio.Button>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                        )}
                    </Row>
                </Form>
            </Modal>
        </>
    );
}
